import { useEffect, useRef, useState } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { RegulifyService } from "../api/RegulifyService";
import { useLoading } from "../context/LoadingContext";
import GraphFilters from "./GraphFilters";

const { prod_codes } = require("../utils/ProdCodes");

const allFilter = { label: "ALL", value: "ALL" };

const GraphViewer = () => {
  const [originalGraphData, setOriginalGraphData] = useState({
    nodes: [],
    links: [],
  });
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [filter, setFilter] = useState(null);
  const [summary, setSummary] = useState("");
  const [productCodes, setProductCodes] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [selectedProductCode, setSelectedProductCode] = useState(allFilter);
  const [selectedApplicant, setSelectedApplicant] = useState(allFilter);
  const [referenceFilter, setReferenceFilter] = useState("");
  const fgRef = useRef();

  const { loading, setLoading } = useLoading();

  useEffect(() => {
    if (filter && filter.medicalSpecialty && filter.productCode) {
      const fetchGraph = async () => {
        try {
          setLoading(true, "Processing your file… This may take several minutes. Thank you for waiting.", "text-green-600");
          const response = await RegulifyService.pdd_generate_kg(
            filter.medicalSpecialty,
            filter.productCode,
            filter.productCode,
          );

          if (
            response?.graph_json_data?.nodes &&
            response.graph_json_data?.links &&
            response.graph_json_data.nodes.length > 0
          ) {
            setSelectedApplicant(allFilter);
            setSelectedProductCode(allFilter);
            setReferenceFilter("");
            setOriginalGraphData(response.graph_json_data);
            setGraphData(response.graph_json_data);
            setSummary(response.summary);
            setTimeout(() => fgRef.current?.zoomToFit(400), 400); // Zoom after rendering
          } else {
            setGraphData({ nodes: [], links: [] });
          }
        } catch (error) {
          console.error("Failed to load graph data", error);
        }

        setLoading(false);
      };

      fetchGraph();
    }
  }, [filter]);

  useEffect(() => {
    if (
      selectedProductCode.label === allFilter.label &&
      selectedApplicant.label === allFilter.label
    ) {
      setReferenceFilter("");
      setApplicants(
        extractGraphFilter(
          originalGraphData.nodes,
          "app_node",
          selectedApplicant.label,
        ),
      );
      setProductCodes(
        extractGraphFilter(
          originalGraphData.nodes,
          "prod_node",
          selectedProductCode.label,
        ),
      );
    } else if (
      selectedProductCode.label !== allFilter.label &&
      selectedApplicant.label === allFilter.label
    ) {
      setReferenceFilter("prod");
      if (selectedProductCode.applicants?.length > 0) {
        setApplicants(selectedProductCode.applicants);
      } else {
        const applicants = extractGraphFilter(
          graphData.nodes,
          "app_node",
          selectedProductCode.label,
        );
        const updatedProductCodes = [...productCodes];
        updatedProductCodes[updatedProductCodes.indexOf(selectedProductCode)] =
          {
            ...selectedProductCode,
            applicants: applicants,
          };
        setProductCodes(updatedProductCodes);
        setApplicants(applicants);
      }
    } else if (
      selectedProductCode.label === allFilter.label &&
      selectedApplicant.label !== allFilter.label
    ) {
      setReferenceFilter("app");
      if (selectedApplicant.products?.length > 0) {
        setProductCodes(selectedApplicant.products);
      } else {
        const products = extractGraphFilter(
          graphData.nodes,
          "prod_node",
          selectedProductCode.label,
        );
        const updatedApplicants = [...applicants];
        updatedApplicants[updatedApplicants.indexOf(selectedApplicant)] = {
          ...selectedApplicant,
          products: products,
        };
        setApplicants(updatedApplicants);
        setProductCodes(products);
      }
    }
  }, [graphData]);

  useEffect(() => {
    if (
      referenceFilter === "app" &&
      selectedApplicant.label !== allFilter.label
    ) {
      setSelectedProductCode(allFilter);
      applyLocalFilter(allFilter.label, selectedApplicant.label);
    } else {
      applyLocalFilter(selectedProductCode.label, selectedApplicant.label);
    }
  }, [selectedApplicant]);

  useEffect(() => {
    if (
      referenceFilter === "prod" &&
      selectedProductCode.label !== allFilter.label
    ) {
      setSelectedApplicant(allFilter);
      applyLocalFilter(selectedProductCode.label, allFilter.label);
    } else {
      applyLocalFilter(selectedProductCode.label, selectedApplicant.label);
    }
  }, [selectedProductCode]);

  const extractGraphFilter = (nodes, type, selected) => {
    const filterValues = [
      ...new Set(
        nodes
          .filter((node) => node.type === type)
          .map((item) => item.label.toUpperCase()),
      ),
    ].sort((a, b) => {
      if (a.nodeName < b.nodeName) return -1;
      if (a.nodeName > b.nodeName) return 1;
      return 0;
    });

    return [allFilter.label].concat(filterValues).map((nodeName, index) => {
      return {
        label: nodeName,
        value: nodeName,
      };
    });
  };

  const handleNodeClick = (node) => {
    if (
      node.type === "510k" &&
      node.color === "black" &&
      node.id.startsWith("DEN")
    ) {
      window.open(
        `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/denovo.cfm?id=${node.id}`,
        "_blank",
      );
    } else if (
      node.type === "510k" &&
      node.color === "black" &&
      node.id.startsWith("K")
    ) {
      window.open(
        `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=${node.id}`,
        "_blank",
      );
    } else if (
      node.type === "510k" &&
      node.color === "black" &&
      node.id.startsWith("P")
    ) {
      window.open(
        `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpma/pma.cfm?id=${node.id + "S001"}`,
        "_blank",
      );
    }
    if (
      node.type === "reg_node" &&
      node.color === "red" &&
      node.id.startsWith("8")
    ) {
      window.open(
        `https://www.ecfr.gov/current/title-21/section-${node.id.substring(0, 8)}`,
        "_blank",
      );
    }
    if (
      node.type === "prod_node" &&
      node.color === "blue" &&
      node.id.length === 3 &&
      prod_codes.indexOf(node.id) >= 0
    ) {
      window.open(
        `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfPCD/classification.cfm?id=${prod_codes.indexOf(node.id) + 1}`,
        "_blank",
      );
    }
  };

  const handleFilterByProdCode = (product) => {
    setSelectedProductCode(product);
  };

  const handleFilterByApplicant = (applicant) => {
    setSelectedApplicant(applicant);
  };

  const applyLocalFilter = (productCode, applicant) => {
    const baseNodes = originalGraphData.nodes.filter(
      (node) =>
        node.label.toUpperCase() === productCode.toUpperCase() ||
        node.label.toUpperCase() === applicant.toUpperCase(),
    );
    if (baseNodes.length > 0) {
      const subgraphData = findSubgraph(baseNodes);
      setGraphData({ nodes: subgraphData.nodes, links: subgraphData.links });
    } else {
      setGraphData(originalGraphData);
    }
  };

  const findSubgraph = (
    startNodes,
    nodes = originalGraphData.nodes,
    links = originalGraphData.links,
  ) => {
    const visitedNodes = new Set(startNodes.map((node) => node.id));
    let relatedNodes = [...startNodes];
    let relatedLinks = [];
    const queue = [...startNodes];

    while (queue.length > 0) {
      const currentNode = queue.shift();

      const connectedLinks = links.filter(
        (link) =>
          link.source.id === currentNode.id ||
          link.target.id === currentNode.id,
      );

      for (const link of connectedLinks) {
        const otherNodeId =
          link.source.id === currentNode.id ? link.target.id : link.source.id;

        const otherNode = nodes.find((node) => node.id === otherNodeId);
        if (
          !otherNode ||
          startNodes.some(
            (node) =>
              node.type === otherNode.type && node.label !== otherNode.label,
          )
        ) {
          continue;
        }

        if (
          !relatedLinks.some(
            (l) =>
              l.source.id === link.source.id && l.target.id === link.target.id,
          )
        ) {
          relatedLinks.push(link);
        }

        if (visitedNodes.has(otherNodeId)) {
          continue;
        }

        visitedNodes.add(otherNodeId);
        relatedNodes.push(otherNode);

        if (
          otherNode.type !== "med_node" &&
          otherNode.type !== "reg_node" &&
          otherNode.type !== "app_node" &&
          otherNode.type !== "prod_node"
        ) {
          queue.push(otherNode);
        }
      }
    }

    if (startNodes.length > 1) {
      const nodes510k = relatedNodes.filter((node) => node.type === "510k");
      for (const node510k of nodes510k) {
        const links520k = relatedLinks.filter(
          (link) => link.source.id === node510k.id,
        );
        let relatedProdNodes = [];
        let relatedAppNodes = [];
        for (const link520k of links520k) {
          relatedProdNodes = [
            ...relatedProdNodes,
            ...relatedNodes.filter(
              (node) =>
                node.id === link520k.target.id && node.type === "prod_node",
            ),
          ];
          relatedAppNodes = [
            ...relatedAppNodes,
            ...relatedNodes.filter(
              (node) =>
                node.id === link520k.target.id && node.type === "app_node",
            ),
          ];
        }
        if (relatedProdNodes.length === 0 || relatedAppNodes.length === 0) {
          relatedNodes = relatedNodes.filter((node) => node.id !== node510k.id);
          relatedLinks = relatedLinks.filter(
            (link) => link.source.id !== node510k.id,
          );
        }
      }
    }

    return { nodes: relatedNodes, links: relatedLinks };
  };

  return (
    <div className="flex w-full flex-col">
      {graphData.nodes.length > 0 && (
        <ForceGraph2D
          ref={fgRef}
          graphData={graphData}
          // nodeAutoColorBy="id"
          nodeColor={(node) => {
            switch (node.type) {
              case "510k":
                return "black";
              case "med_node":
                return "green";
              case "reg_node":
                return "red";
              case "prod_node":
                return "blue";
              default:
                return "gray";
            }
          }}
          nodeLabel={(node) => `${node.label || node.id}`}
          linkDirectionalArrowLength={6}
          linkDirectionalArrowRelPos={1}
          nodeCanvasObjectMode={() => "after"}
          onNodeClick={handleNodeClick}
          nodeCanvasObject={(node, ctx, globalScale) => {
            const label = node.label || node.id;
            const fontSize = 12 / globalScale;
            ctx.font = `${fontSize}px Sans-Serif`;
            ctx.fillStyle = "black";
            ctx.fillText(label, node.x + 6, node.y + 6);
          }}
        />
      )}
      {graphData.nodes.length === 0 && !loading && (
        <div className="base-200 mt-80 flex justify-center p-4">
          No results for current search
        </div>
      )}
      <GraphFilters
        summary={summary}
        setFilter={setFilter}
        productCodes={productCodes}
        applicants={applicants}
        selectedProductCode={selectedProductCode}
        selectedApplicant={selectedApplicant}
        handleFilterByProdCode={handleFilterByProdCode}
        handleFilterByApplicant={handleFilterByApplicant}
      />
    </div>
  );
};

export default GraphViewer;
